/** Renders the boundary between the blocks stitched into one transcript row (ADR 0001). */

import { createContext, useContext } from 'react'
import { DirectiveDescriptor } from '@mdxeditor/editor'
import type { LeafDirective } from 'mdast-util-directive'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { themeConfig } from '@gap/shared/theme'
import {
   formatSessionBlockLabel,
   GAP_SESSION_HEADER_DIRECTIVE,
   readSessionOrdinal,
   readSessionSource,
   SESSION_PREAMBLE_LABEL,
   type SessionLayout,
} from '@gap/shared/meetings/session-header'

const EMPTY_SESSION_LAYOUT: SessionLayout = { startedAts: [], hasPreamble: false, blockCount: 0, hasMultipleSessions: false }

/** The document's session structure, parsed by `Markdown.tsx` so each header derives its ordinal. */
export const SessionLayoutContext = createContext<SessionLayout>(EMPTY_SESSION_LAYOUT)

const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

/** One block's boundary marker: the label on the left, a hairline filling the rest. */
function SessionMarker({ label }: { label: string }) {
   return (
      <Box
         contentEditable={false}
         data-gap-session-header="true"
         sx={{
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 2,
            mb: 1,
         }}
      >
         <Typography variant="overline" sx={{ color: themeConfig.primary[600], whiteSpace: 'nowrap' }}>
            {label}
         </Typography>
         <Box sx={{ flex: 1, borderBottom: `1px solid ${themeConfig.primary[200]}` }} />
      </Box>
   )
}

/** Marks transcript content that no Recording Session produced, such as a pasted transcript. */
export function SessionPreambleMarker({ layout }: { layout: SessionLayout }) {
   if (!layout.hasPreamble || !layout.hasMultipleSessions) return null
   return <SessionMarker label={SESSION_PREAMBLE_LABEL} />
}

function GapSessionHeaderComponent({ mdastNode }: { mdastNode: LeafDirective }) {
   const layout = useContext(SessionLayoutContext)
   const attributes = mdastNode.attributes as Record<string, string> | undefined
   const startedAt = attributes?.startedAt

   // A lone block renders no marker (matches legacy single-session meetings).
   if (!layout.hasMultipleSessions) return null
   // A header with no start is unplaceable, so the PDF strips it and so does the editor.
   if (!startedAt) return null

   const label = formatSessionBlockLabel({
      header: { startedAt, endedAt: attributes?.endedAt, source: readSessionSource(attributes?.source) },
      ordinal: readSessionOrdinal({ layout, startedAt }),
      timezone: viewerTimezone,
   })

   return <SessionMarker label={label} />
}

export const GapSessionHeaderDirectiveDescriptor: DirectiveDescriptor<LeafDirective> = {
   name: GAP_SESSION_HEADER_DIRECTIVE,
   type: 'leafDirective',
   testNode(node) {
      return node.name === GAP_SESSION_HEADER_DIRECTIVE
   },
   attributes: ['startedAt', 'endedAt', 'source'],
   hasChildren: false,
   Editor: GapSessionHeaderComponent,
}
